<template>
  <Menu as="div" class="relative inline-block text-left" v-slot="{ open }">
    <div>
      <MenuButton ref="reference">
        <slot name="trigger" />
      </MenuButton>
    </div>

    <Teleport to="body">
      <transition
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-95 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-95 opacity-0"
      >
        <MenuItems
          v-show="open"
          ref="floating"
          :style="{
            position: strategy,
            top: `${y ?? 0}px`,
            left: `${x ?? 0}px`,
            zIndex: 30
          }"
          class="w-56 origin-top-right divide-y divide-yapgray-200 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none dark:divide-yapdarkgray-300 dark:border dark:border-yapdarkgray-400 dark:bg-yapdarkgray-700"
        >
          <template v-if="!!entries">
            <div class="px-1 py-1" v-for="group in entries" :key="group.id">
              <MenuItem v-slot="{ active }" v-for="item in group.items" :key="item.id">
                <router-link
                  v-if="item.to"
                  :to="item.to"
                  :class="[
                    'flex w-full items-center gap-2 px-2 py-2 text-sm',
                    active
                      ? item.hover || 'text-primary-500 dark:text-primary-dark-300'
                      : 'text-gray-700 dark:text-yapgray-100'
                  ]"
                >
                  <YapTooltip :text="item.tooltip">
                    <span class="flex items-center gap-2">
                      <YapIcon :name="item.icon" v-if="item.icon" class="h-7 w-7" />
                      <span v-if="item.name">{{ $t(item.name) }}</span>
                    </span>
                  </YapTooltip>
                </router-link>
                <button
                  :class="[
                    'group flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm',
                    item.disabled
                      ? 'cursor-not-allowed opacity-50'
                      : active
                        ? item.hover || 'text-primary-500 dark:text-primary-dark-300'
                        : 'text-gray-700 dark:text-yapgray-100'
                  ]"
                  @click="item.action"
                  :disabled="item.disabled"
                  v-else
                >
                  <YapTooltip :text="item.tooltip">
                    <span class="flex items-center gap-2">
                      <YapIcon :name="item.icon" v-if="item.icon" class="h-7 w-7" />
                      <span v-if="item.name">{{ t(item.name) }}</span>
                    </span>
                  </YapTooltip>
                </button>
              </MenuItem>
            </div>
          </template>
          <template v-else>
            <slot name="content" />
          </template>
        </MenuItems>
      </transition>
    </Teleport>
  </Menu>
</template>

<script lang="ts">
import { ContextType } from '@/router/routes';

export interface DropdownEntry {
  id: string;
  name?: string;
  icon?: string;
  to?: { name: string; params?: { id: string } };
  as?: string;
  meta?: {
    title?: string;
    requiresAuth?: boolean;
    contexts: ContextType[];
  };
  hover?: string;
  action?(): any;
  disabled?: boolean; // only for button actions
  tooltip?: string;
}

export interface DropdownGroup {
  id: string;
  items: Array<DropdownEntry>;
}
</script>

<script setup lang="ts">
import { ref } from 'vue';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import { useFloating, offset, flip, shift, autoUpdate, Placement } from '@floating-ui/vue';
import YapIcon from '@/components/YapIcon.vue';
import YapTooltip from '@/components/YapTooltip.vue';
import { useI18n } from '@/plugins/i18n';

const { t } = useI18n();

const props = defineProps<{
  entries?: Array<DropdownGroup>;
  placement?: Placement;
}>();

const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);

const { x, y, strategy } = useFloating(reference, floating, {
  placement: props.placement ?? 'bottom-end',
  middleware: [offset(8), flip(), shift()],
  whileElementsMounted: autoUpdate
});
</script>
